import { z } from 'zod';
import { CardType } from '../../../payment.types';
import {
    type ValorCardTokenizationResponse,
    type ValorBrowserTokenizationResultV2,
    ValorTokenizationResultType,
    type ValorBrowserTokenizationSession,
    ValorBrowserTokenizationSessionType,
    type ValorDelegateTokenResponse,
    type ValorGatewayTransactionCardMetadata,
    type ValorGatewayTransactionDetails,
    type ValorGatewayTransactionDetailsV1,
    type ValorGatewayTransactionDetailsV2,
    ValorGatewayTransactionDetailsType,
    type ValorGatewayTransactionTaxComponent,
    type ValorGatewayTransactionTaxDetails,
} from '../../../types/ipg/valor/valor-payment.types';
import { ValorGatewayId } from '../../../types/ipg/valor/valor-system-constants';
import {
    JsonNonnegativeLosslessNumberInputSchema,
    NonEmptyStringSchema,
    PositiveIntSchema,
    JsonObjectSchema,
} from '../../common.schemas';

export const ValorDelegateTokenResponseSchema: z.ZodType<ValorDelegateTokenResponse> = z.object({
    client_token: NonEmptyStringSchema,
    validity: NonEmptyStringSchema,
    domain: NonEmptyStringSchema,
    epi: NonEmptyStringSchema,
});

export const ValorBrowserTokenizationSessionSchema: z.ZodType<ValorBrowserTokenizationSession> =
    z.object({
        client_token: NonEmptyStringSchema,
        validity: NonEmptyStringSchema,
        domain: NonEmptyStringSchema,
        epi: NonEmptyStringSchema,
        browser_tokenization_session_type: z
            .literal(ValorBrowserTokenizationSessionType)
            .optional(),
    });

export const ValorCardTypeSchema: z.ZodType<CardType> = z.union([
    z.literal(CardType.DEBIT),
    z.literal(CardType.CREDIT),
]);

export const ValorCardTokenizationResponseSchema: z.ZodType<ValorCardTokenizationResponse> = z
    .object({
        error_no: z.literal('S00'),
        error_code: z.literal('S00'),
        cardToken: NonEmptyStringSchema,
        card_brand: NonEmptyStringSchema,
        card_type: NonEmptyStringSchema.optional(),
        response: z.null(),
    })
    .strict();

export function parseValorCardTokenizationFunctionalResponse(
    providerResponse: z.infer<typeof JsonObjectSchema>
): ValorCardTokenizationResponse {
    return ValorCardTokenizationResponseSchema.parse({
        error_no: providerResponse.error_no,
        error_code: providerResponse.error_code,
        cardToken: providerResponse.cardToken,
        card_brand: providerResponse.card_brand,
        card_type: providerResponse.card_type,
        response: providerResponse.response,
    });
}

export const ValorBrowserTokenizationResultV2Schema = z
    .object({
        result_type: z.literal(ValorTokenizationResultType.BROWSER_TOKENIZATION_V2),
        provider_request: JsonObjectSchema,
        provider_response: JsonObjectSchema,
        token: NonEmptyStringSchema,
        card_brand: NonEmptyStringSchema,
        card_type: NonEmptyStringSchema.optional(),
    })
    .strict()
    .superRefine((value, context) => {
        if (value.provider_response.cardToken !== value.token) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Valor tokenization result does not match its provider response.',
                path: ['token'],
            });
        }
        if (value.provider_response.card_brand !== value.card_brand) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Valor tokenization card brand does not match its provider response.',
                path: ['card_brand'],
            });
        }
        if (
            value.card_type !== undefined &&
            value.provider_response.card_type !== value.card_type
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Valor tokenization card type does not match its provider response.',
                path: ['card_type'],
            });
        }
    }) satisfies z.ZodType<ValorBrowserTokenizationResultV2>;

const ValorGatewayTransactionCoreSchemaBase = z.object({
    gateway_id: z.literal(ValorGatewayId),
    gateway_transaction_details_type: z.literal(ValorGatewayTransactionDetailsType),
    gateway_transaction_details_version: PositiveIntSchema,
    gateway_txn_id: NonEmptyStringSchema,
    base_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_tax_amount: JsonNonnegativeLosslessNumberInputSchema,
    is_void: z.boolean(),
    is_settled: z.boolean(),
    refund_amount: JsonNonnegativeLosslessNumberInputSchema,
    refund_count: z.number().int().nonnegative(),
    card_brand: NonEmptyStringSchema.optional(),
    card_last4: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    cardholder_name: NonEmptyStringSchema.optional(),
    card_type: ValorCardTypeSchema.optional(),
});

export const ValorGatewayTransactionTaxComponentSchema: z.ZodType<ValorGatewayTransactionTaxComponent> =
    z.object({
        rate: JsonNonnegativeLosslessNumberInputSchema,
        amount: JsonNonnegativeLosslessNumberInputSchema,
        taxable_sales: JsonNonnegativeLosslessNumberInputSchema,
    });

export const ValorGatewayTransactionTaxDetailsSchema: z.ZodType<ValorGatewayTransactionTaxDetails> =
    z.object({
        state_tax: ValorGatewayTransactionTaxComponentSchema.optional(),
        city_tax: ValorGatewayTransactionTaxComponentSchema.optional(),
        reduced_tax: ValorGatewayTransactionTaxComponentSchema.optional(),
    });

export const ValorGatewayTransactionCardMetadataSchema: z.ZodType<ValorGatewayTransactionCardMetadata> =
    z.object({
        card_bin: z
            .string()
            .regex(/^\d{6}$/)
            .optional(),
        card_type: ValorCardTypeSchema.optional(),
        issuing_bank: NonEmptyStringSchema.optional(),
        country: NonEmptyStringSchema.optional(),
        card_level: NonEmptyStringSchema.optional(),
        card_category: NonEmptyStringSchema.optional(),
    });

const ValorGatewayTransactionDetailsSchemaBase = ValorGatewayTransactionCoreSchemaBase.extend({
    custom_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
    card_scheme: NonEmptyStringSchema.optional(),
    pan: z.string().optional(),
    rrn: NonEmptyStringSchema.optional(),
    stan_no: NonEmptyStringSchema.optional(),
    batch_no: NonEmptyStringSchema.optional(),
    auth_code: NonEmptyStringSchema.optional(),
    card_metadata: ValorGatewayTransactionCardMetadataSchema.optional(),
    tax_details: ValorGatewayTransactionTaxDetailsSchema.optional(),
});

export const ValorGatewayTransactionDetailsV1Schema: z.ZodType<ValorGatewayTransactionDetailsV1> =
    ValorGatewayTransactionDetailsSchemaBase.extend({
        gateway_transaction_details_version: z.literal(1),
    });

export const ValorGatewayTransactionDetailsV2Schema: z.ZodType<ValorGatewayTransactionDetailsV2> =
    ValorGatewayTransactionDetailsSchemaBase.extend({
        gateway_transaction_details_version: z.literal(2),
        tip_amount: JsonNonnegativeLosslessNumberInputSchema,
    });

export const ValorGatewayTransactionDetailsSchema: z.ZodType<ValorGatewayTransactionDetails> =
    z.union([ValorGatewayTransactionDetailsV2Schema, ValorGatewayTransactionDetailsV1Schema]);
